import { objects, strings } from 'investira.sdk';
import { getEmpresasFilterConfig } from './empresasFilterRegistry';

/** Informa se o valor representa a ausência de um filtro. */
function pvIsEmptyFilterValue(pValue) {
  return pValue === null || pValue === undefined || pValue === '';
}

/** Normaliza um valor conforme o contrato declarado no registry. */
function pvNormalizeValue(pValue, pDefinition) {
  if (pvIsEmptyFilterValue(pValue)) {
    return null;
  }

  if (pDefinition.type === 'bitmask') {
    const xValue = Number(pValue);

    if (!Number.isInteger(xValue) || xValue <= 0) {
      return null;
    }

    const xMask = pDefinition.values.reduce(
      (pMask, pOptionValue) =>
        (xValue & pOptionValue) === pOptionValue ? pMask + pOptionValue : pMask,
      0
    );

    return xMask || null;
  }

  if (pDefinition.type === 'number') {
    const xValue = Number(pValue);

    if (
      !Number.isFinite(xValue) ||
      (pDefinition.minimum !== undefined && xValue < pDefinition.minimum)
    ) {
      return null;
    }

    return xValue;
  }

  if (pDefinition.type === 'enum') {
    return pDefinition.values.includes(pValue) ? pValue : null;
  }

  return pValue;
}

/** Mantém somente os parâmetros válidos pertencentes ao contrato informado. */
function pvNormalizeParams(pValues, pDefinitions) {
  return Object.keys(pDefinitions).reduce((pResult, pParam) => {
    const xValue = pvNormalizeValue(pValues[pParam], pDefinitions[pParam]);

    // O valor zero é válido para os filtros financeiros.
    if (!pvIsEmptyFilterValue(xValue)) {
      pResult[pParam] = xValue;
    }

    return pResult;
  }, {});
}

/** Retorna o campo e o contrato do filtro de tipo de empresa. */
function pvGetPessoaTipoConfig() {
  const xConfig = getEmpresasFilterConfig();
  const xField = xConfig.fields.find(pField => pField.name === 'pessoa_tipo_id');

  return { field: xField, query: xConfig.queryParams.pessoa_tipo_id };
}

/** Retorna as opções de tipo presentes na máscara recebida. */
export function readPessoaTipoOptions(pValue) {
  const { field: xField, query: xDefinition } = pvGetPessoaTipoConfig();
  const xMask = pvNormalizeValue(pValue, xDefinition);

  if (!xMask) {
    return [];
  }

  return xField.options.filter(pOption => (xMask & pOption.value) === pOption.value);
}

/** Converte a máscara da URL no array controlado pelo multiselect. */
export function readPessoaTipoValues(pValue) {
  return readPessoaTipoOptions(pValue).map(pOption => pOption.value);
}

/** Converte os valores selecionados na máscara numérica esperada pela API. */
export function createPessoaTipoMask(pValues = []) {
  const { query: xDefinition } = pvGetPessoaTipoConfig();

  const xMask = [...new Set(pValues)]
    .filter(pValue => xDefinition.values.includes(pValue))
    .reduce((pMask, pValue) => pMask + pValue, 0);

  return pvNormalizeValue(xMask, xDefinition);
}

/** Lê a query atual e normaliza o filtro e a ordenação de empresas. */
export function readEmpresasFilterQuery(pSearch = '') {
  const xQuery = strings.querystringToObject(pSearch) || {};

  return pvNormalizeParams(xQuery, getEmpresasFilterConfig().queryParams);
}

/** Atualiza os filtros de empresas sem descartar pesquisa ou ordenação. */
export function updateEmpresasFilterQuery(pQuery = {}, pUpdates = {}) {
  const xQuery = { ...pQuery, ...pUpdates };

  return pvNormalizeParams(xQuery, getEmpresasFilterConfig().queryParams);
}

/** Serializa os parâmetros preservados para a URL. */
export function serializeEmpresasFilterQuery(pQuery = {}) {
  const xQuery = pvNormalizeParams(pQuery, getEmpresasFilterConfig().queryParams);

  return Object.keys(xQuery).length ? objects.objectToQueryString(xQuery) : '';
}

/** Remove o segmento de paginação após uma alteração de filtro. */
export function removeEmpresasPaginationFromPath(pPathname = '') {
  return pPathname.replace(/\/page\/\d+$/, '');
}

/** Garante que a listagem encaminhe filtro e ordenação válidos ao serviço. */
export function sanitizeEmpresasListParams(pParams = {}) {
  return pvNormalizeParams(pParams, getEmpresasFilterConfig().requestParams);
}

/** Monta a ordenação final da API com desempates estáveis para cada listagem. */
export function resolveEmpresasListSort(pSort, pHasSearch, pHasSetor = false) {
  const xSetorSort = pHasSetor ? '' : 'top_setor_id ASC, ';

  if (pSort) {
    const xPessoaSort = pSort.startsWith('pessoa_fantasia ') ? '' : ', pessoa_fantasia ASC';

    return `${pSort}, relevancia DESC, ${xSetorSort}setor_id DESC${xPessoaSort}`;
  }

  return pHasSearch
    ? `relevancia DESC, __mr__ DESC, ${xSetorSort}setor_id DESC, pessoa_fantasia ASC`
    : `relevancia DESC, ${xSetorSort}setor_id DESC, pessoa_fantasia ASC`;
}
